import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * EU-709 — catálogo dos tipos de vaga/programa.
 *
 * RN-01 exige que `Tipo de vaga` e `Tipo de programa` contenham SEMPRE os
 * mesmos valores. Até esta entrega cada tela carregava a própria lista
 * hardcoded (`CONTRACT_TYPE_OPTIONS`, quatro itens, duplicada na criação e na
 * edição de programa) e nada impedia as duas de divergirem. Agora todas leem
 * daqui.
 *
 * Cada item traz DOIS identificadores porque as duas superfícies falam
 * vocabulários diferentes com suas próprias APIs: as telas de VAGA persistem o
 * `token` PT-BR, as de PROGRAMA persistem a `key` do catálogo. Cada tela pega o
 * identificador que a sua API entende, sem traduzir nada aqui.
 */
export interface ContractTypeOption {
  id: number;
  /** `core.contract_type.key` — o que as telas de PROGRAMA persistem. */
  key: string;
  /** Token PT-BR — o que as telas de VAGA persistem. `null` para tipo sem token. */
  token: string | null;
  /** Rótulo canônico. A lista já vem ordenada por ele. */
  label: string;
  requirements: {
    contractTypeKey: string | null;
    optionalSections: string[];
    requiredFields: Record<string, string[]>;
  };
}

export class ContractTypesFetchError extends Error {
  readonly name = 'ContractTypesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchContractTypes(signal?: AbortSignal): Promise<ContractTypeOption[]> {
  const res = await fetchClient<{ items: ContractTypeOption[] }>({
    method: 'GET',
    url: '/catalogues/contract-types',
    signal,
  });
  if (res.error) {
    throw new ContractTypesFetchError(
      `Failed to load contract types (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data.items;
}

/**
 * O catálogo é tabela de referência: muda por migration, não por uso. Cache
 * longo e sem refetch por foco — reconsultar a cada troca de aba só gastaria
 * request para receber a mesma lista.
 */
export function contractTypesQueryOptions() {
  return queryOptions({
    queryKey: ['recruiter', 'catalogues', 'contract-types'] as const,
    queryFn: ({ signal }) => fetchContractTypes(signal),
    staleTime: 30 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ContractTypesFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
